export type AIToolTier = 'quick-prompt' | 'agent' | 'workflow';
export type AIToolAccess = 'free' | 'pro' | 'premium';

export type AgentNodeType = 
  | 'orchestrator'
  | 'specialist' 
  | 'aggregator'
  | 'validator'
  | 'iterator'
  | 'researcher';

export interface PromptVariable {
  id: string;
  name: string;
  placeholder: string;
  description?: string;
  required?: boolean;
  defaultValue?: string;
}

export interface AIToolBase {
  id: string;
  name: string;
  description: string;
  tier: AIToolTier;
  access: AIToolAccess;
  icon: string;
  color: string;
  category: string;
  tags: string[];
  createdAt: string;
  featured?: boolean;
  createdBy?: 'system' | 'user';
}

export interface QuickPrompt extends AIToolBase {
  tier: 'quick-prompt';
  promptTemplate: string;
  variables: PromptVariable[];
  outputFormat?: 'text' | 'markdown' | 'json' | 'list';
}

export type CapabilityModuleType = 
  | 'research'
  | 'analysis'
  | 'writing'
  | 'coding'
  | 'data-processing'
  | 'web-search'
  | 'document-parsing'
  | 'summarization'
  | 'translation'
  | 'fact-checking'
  | 'github-repo'
  | 'ui-audit'
  | 'market-research'
  | 'custom';

export interface CapabilityModule {
  id: string;
  type: CapabilityModuleType;
  name: string;
  description: string;
  icon: string;
  color: string;
  config?: {
    maxSteps?: number;
    sources?: string[];
    outputFormat?: string;
    customPrompt?: string;
  };
  isBuiltIn: boolean;
}

export interface AgentPersona {
  role: string;
  expertise: string[];
  communicationStyle: string;
  systemPrompt: string;
}

export interface AgentMemoryPolicy {
  type: 'none' | 'session' | 'persistent';
  retentionDays?: number;
  maxMemoryItems?: number;
  includeContext?: boolean;
}

export interface AgentCollaborationRules {
  canDelegate: boolean;
  canReceiveDelegation: boolean;
  handoffProtocol?: string;
  sharedContextKeys?: string[];
}

export type AgentOutputFormat = 'text' | 'markdown' | 'json' | 'list';

export interface AgentOutputRequirements {
  format?: AgentOutputFormat;
  instructions?: string;
  requiredSections?: string[];
  maxLengthHint?: string;
}

export interface Agent extends AIToolBase {
  tier: 'agent';
  nodeType: AgentNodeType;
  persona: AgentPersona;
  capabilityModules: string[];
  memoryPolicy: AgentMemoryPolicy;
  collaborationRules?: AgentCollaborationRules;
  preferredModel?: string;
  contextWindow?: number;
  maxIterations?: number;
  isTemplate?: boolean;
  composedAgentIds?: string[];
  folderId?: string | null;
  lens?: string | null;
  expertFocus?: string | null;
  outputRequirements?: AgentOutputRequirements;
  linkedSourceIds?: string[];
  trainingDocumentIds?: string[];
  integrationScopes?: string[];
  executionMode?: 'manual' | 'automatic' | 'both';
  outputSchema?: Record<string, unknown>;
  stepRole?: string | null;
  actionPermissions?: Record<string, unknown>;
  confidencePolicy?: { minScore?: number; onLowConfidence?: 'branch' | 'pause' | 'continue' };
  qualityPolicy?: { minScore?: number; onLowQuality?: 'branch' | 'pause' | 'continue' };
  attachedSkillIds?: string[];
  customInstructions?: string | null;
  workerProfileId?: string | null;
  allowedToolIds?: string[];
}

export type WorkflowNodeType =
  | 'trigger'
  | 'prompt'
  | 'agent'
  | 'crew'
  | 'tool'
  | 'knowledge'
  | 'parallel'
  | 'merge'
  | 'condition'
  | 'loop'
  | 'delay'
  | 'http-request'
  | 'transform'
  | 'output'
  | 'human-review'
  | 'validation'
  | 'entity-resolution'
  | 'confidence-gate'
  | 'quality-gate';

export type WorkflowTriggerType = 
  | 'manual'
  | 'scheduled'
  | 'webhook'
  | 'event'
  | 'chat-mention';

export interface WorkflowTrigger {
  type: WorkflowTriggerType;
  config: {
    schedule?: string;
    webhookPath?: string;
    eventType?: string;
    mentionKeyword?: string;
  };
}

export interface WorkflowNode {
  id: string;
  type: WorkflowNodeType;
  position: { x: number; y: number };
  data: {
    label: string;
    description?: string;
    agentId?: string;
    promptId?: string;
    promptTemplate?: string;
    condition?: string;
    conditionValue?: boolean | string | number;
    loopCount?: number;
    delayMs?: number;
    httpConfig?: {
      url: string;
      method: string;
      headers?: Record<string, string>;
      body?: string;
    };
    transformScript?: string;
    outputFormat?: string;
    variables?: PromptVariable[];
    toolId?: string;
    toolOperation?: string;
    toolInputMapping?: Record<string, string>;
    knowledgeSource?: 'kb' | 'thread' | 'integration' | 'space' | 'trigger';
    knowledgeConfig?: Record<string, unknown>;
    validationSchema?: Record<string, unknown>;
    confidenceThreshold?: number;
    qualityThreshold?: number;
    mergeStrategy?: 'all' | 'any' | 'first';
    skillIds?: string[];
    workerProfileId?: string;
    /** 4A.7: max manager delegation rounds for a crew node (bounded). */
    crewMaxRounds?: number;
  };
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  label?: string;
  condition?: string;
  dataMapping?: Record<string, string>;
}

export interface Workflow extends AIToolBase {
  tier: 'workflow';
  trigger: WorkflowTrigger;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  entryNodeId: string;
  variables?: PromptVariable[];
  isBlueprint?: boolean;
  blueprintCategory?: string;
  estimatedDuration?: string;
  estimatedCost?: string;
  lastRunAt?: string;
  runCount?: number;
  status?: 'draft' | 'active' | 'paused' | 'archived';
  publishedVersionId?: string | null;
  draftDirty?: boolean;
}

export type AITool = QuickPrompt | Agent | Workflow;

export const AI_TOOL_CATEGORIES = [
  'All',
  'Writing',
  'Research',
  'Analysis',
  'Creative',
  'Academic',
  'Business',
  'Productivity',
  'Learning',
  'Personal',
  'Technical'
];

export const CAPABILITY_MODULES: CapabilityModule[] = [
  {
    id: 'cap-research',
    type: 'research',
    name: 'Deep Research',
    description: 'Multi-step research with source gathering and synthesis',
    icon: 'Search',
    color: 'bg-blue-500',
    isBuiltIn: true,
    config: { maxSteps: 5 }
  },
  {
    id: 'cap-analysis',
    type: 'analysis',
    name: 'Data Analysis',
    description: 'Analyze data, find patterns, and generate insights',
    icon: 'BarChart3',
    color: 'bg-purple-500',
    isBuiltIn: true
  },
  {
    id: 'cap-writing',
    type: 'writing',
    name: 'Content Writing',
    description: 'Create articles, documents, and long-form content',
    icon: 'PenTool',
    color: 'bg-green-500',
    isBuiltIn: true
  },
  {
    id: 'cap-coding',
    type: 'coding',
    name: 'Code Generation',
    description: 'Write, review, and debug code in multiple languages',
    icon: 'Code2',
    color: 'bg-orange-500',
    isBuiltIn: true
  },
  {
    id: 'cap-web-search',
    type: 'web-search',
    name: 'Web Search',
    description: 'Search the web for current information',
    icon: 'Globe',
    color: 'bg-cyan-500',
    isBuiltIn: true
  },
  {
    id: 'cap-doc-parsing',
    type: 'document-parsing',
    name: 'Document Parsing',
    description: 'Extract and process content from PDFs, docs, and files',
    icon: 'FileText',
    color: 'bg-amber-500',
    isBuiltIn: true
  },
  {
    id: 'cap-summarization',
    type: 'summarization',
    name: 'Summarization',
    description: 'Condense long content into key points',
    icon: 'ListChecks',
    color: 'bg-teal-500',
    isBuiltIn: true
  },
  {
    id: 'cap-translation',
    type: 'translation',
    name: 'Translation',
    description: 'Translate content between languages',
    icon: 'Languages',
    color: 'bg-indigo-500',
    isBuiltIn: true
  },
  {
    id: 'cap-fact-checking',
    type: 'fact-checking',
    name: 'Fact Checking',
    description: 'Verify claims and check accuracy of information',
    icon: 'ShieldCheck',
    color: 'bg-red-500',
    isBuiltIn: true
  },
  {
    id: 'cap-github-repo',
    type: 'github-repo',
    name: 'GitHub Repository',
    description: 'Access and analyze connected GitHub repositories - structure, dependencies, code',
    icon: 'GitBranch',
    color: 'bg-gray-800',
    config: {
      sources: ['github-api'],
      outputFormat: 'structured'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-ui-audit',
    type: 'ui-audit',
    name: 'UI/UX Audit',
    description: 'Analyze frontend components, styling patterns, and user experience flows',
    icon: 'Layout',
    color: 'bg-pink-500',
    config: {
      outputFormat: 'detailed'
    },
    isBuiltIn: true
  },
  {
    id: 'cap-market-research',
    type: 'market-research',
    name: 'Market Research',
    description: 'Research competitors, market positioning, and industry trends',
    icon: 'TrendingUp',
    color: 'bg-emerald-500',
    config: {
      sources: ['web', 'news', 'product-hunt'],
      outputFormat: 'structured'
    },
    isBuiltIn: true
  }
];

export const AGENT_NODE_TYPE_INFO: Record<AgentNodeType, { label: string; description: string; icon: string; color: string }> = {
  orchestrator: {
    label: 'Orchestrator',
    description: 'Controls flow and delegates tasks to other agents',
    icon: 'Crown',
    color: 'bg-yellow-500'
  },
  specialist: {
    label: 'Specialist',
    description: 'Deep expertise in a specific domain or task',
    icon: 'Brain',
    color: 'bg-blue-500'
  },
  aggregator: {
    label: 'Aggregator',
    description: 'Combines and synthesizes outputs from multiple agents',
    icon: 'Layers',
    color: 'bg-purple-500'
  },
  validator: {
    label: 'Validator',
    description: 'Quality checks and validates outputs before proceeding',
    icon: 'CheckCircle',
    color: 'bg-green-500'
  },
  iterator: {
    label: 'Iterator',
    description: 'Loops and refines until criteria are met',
    icon: 'RefreshCw',
    color: 'bg-orange-500'
  },
  researcher: {
    label: 'Researcher',
    description: 'Gathers information from multiple sources',
    icon: 'Search',
    color: 'bg-cyan-500'
  }
};

export const WORKFLOW_NODE_TYPE_INFO: Record<WorkflowNodeType, { label: string; description: string; icon: string; color: string }> = {
  trigger: {
    label: 'Trigger',
    description: 'Start point of the workflow',
    icon: 'Zap',
    color: 'bg-green-500'
  },
  prompt: {
    label: 'Prompt',
    description: 'Execute a single prompt',
    icon: 'MessageSquare',
    color: 'bg-blue-500'
  },
  agent: {
    label: 'Agent',
    description: 'Run an AI agent',
    icon: 'Brain',
    color: 'bg-purple-500'
  },
  crew: {
    label: 'Crew',
    description: 'Manager delegates to worker agents until the goal is met',
    icon: 'Users',
    color: 'bg-fuchsia-500'
  },
  tool: {
    label: 'Tool',
    description: 'Invoke a registered tool operation',
    icon: 'Wrench',
    color: 'bg-violet-500'
  },
  knowledge: {
    label: 'Knowledge',
    description: 'Load context from KB, thread, or integration',
    icon: 'BookOpen',
    color: 'bg-teal-500'
  },
  parallel: {
    label: 'Parallel',
    description: 'Fork execution to multiple branches',
    icon: 'GitFork',
    color: 'bg-sky-500'
  },
  merge: {
    label: 'Merge',
    description: 'Join parallel branches',
    icon: 'GitMerge',
    color: 'bg-sky-600'
  },
  condition: {
    label: 'Condition',
    description: 'Branch based on a condition',
    icon: 'GitBranch',
    color: 'bg-yellow-500'
  },
  loop: {
    label: 'Loop',
    description: 'Repeat a set of steps',
    icon: 'Repeat',
    color: 'bg-orange-500'
  },
  delay: {
    label: 'Delay',
    description: 'Wait before continuing',
    icon: 'Clock',
    color: 'bg-gray-500'
  },
  'http-request': {
    label: 'HTTP Request',
    description: 'Make an external API call',
    icon: 'Globe',
    color: 'bg-cyan-500'
  },
  transform: {
    label: 'Transform',
    description: 'Transform data between steps',
    icon: 'Shuffle',
    color: 'bg-indigo-500'
  },
  output: {
    label: 'Output',
    description: 'Final output of the workflow',
    icon: 'ArrowRight',
    color: 'bg-emerald-500'
  },
  'human-review': {
    label: 'Human Review',
    description: 'Pause for human approval',
    icon: 'UserCheck',
    color: 'bg-pink-500'
  },
  validation: {
    label: 'Validation',
    description: 'Validate output against schema',
    icon: 'CheckSquare',
    color: 'bg-lime-500'
  },
  'entity-resolution': {
    label: 'Entity Resolution',
    description: 'Resolve people, dates, and IDs',
    icon: 'Users',
    color: 'bg-amber-500'
  },
  'confidence-gate': {
    label: 'Confidence Gate',
    description: 'Branch on confidence score',
    icon: 'Gauge',
    color: 'bg-rose-500'
  },
  'quality-gate': {
    label: 'Quality Gate',
    description: 'Branch on quality score',
    icon: 'Star',
    color: 'bg-fuchsia-500'
  }
};

export const WORKFLOW_TRIGGER_TYPE_INFO: Record<WorkflowTriggerType, { label: string; description: string; icon: string }> = {
  manual: {
    label: 'Manual',
    description: 'Run manually by clicking a button',
    icon: 'Play'
  },
  scheduled: {
    label: 'Scheduled',
    description: 'Run on a schedule (cron)',
    icon: 'Calendar'
  },
  webhook: {
    label: 'Webhook',
    description: 'Triggered by an external webhook',
    icon: 'Webhook'
  },
  event: {
    label: 'Event',
    description: 'Triggered by an internal event',
    icon: 'Bell'
  },
  'chat-mention': {
    label: 'Chat Mention',
    description: 'Triggered when mentioned in chat',
    icon: 'AtSign'
  }
};
